import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Save, Upload, Trash2, Copy, DatabaseBackup, RotateCcw, Cpu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, setAccessKey, errMsg, saveBlob } from "@/lib/api";
import { fileToDataUrl, todayISO } from "@/lib/format";
import { useApp } from "@/context/AppCtx";
import { PageHeader } from "@/components/kit";

const F = ({ label, children, className = "" }) => <div className={`space-y-1.5 ${className}`}><Label className="label-cap">{label}</Label>{children}</div>;

export default function Settings() {
  const { settings, setSettings, reload } = useApp();
  const nav = useNavigate();
  const [f, setF] = useState({ ...settings, phones: settings.phones.join("\n"), emails: settings.emails.join("\n") });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e }));
  const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

  const goTo = (slug) => {
    setAccessKey(slug);
    nav(`/${slug}/configuracion`, { replace: true });
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...f, phones: lines(f.phones), emails: lines(f.emails), iva: Number(f.iva) || 0, exchange_rate: Number(f.exchange_rate) || 1 };
      const { data } = await api.put("/settings", body);
      setSettings(data);
      toast.success("Configuración guardada");
      if (data.secret_path !== settings.secret_path) {
        toast.info(`Nueva dirección del sistema: /${data.secret_path}`);
        goTo(data.secret_path);
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const logo = await fileToDataUrl(file, 600);
      setF((x) => ({ ...x, logo }));
      toast.info("Logo cargado. Presione Guardar para aplicarlo.");
    }
  };

  const backup = async () => {
    const { data } = await api.get("/backup");
    saveBlob(new Blob([JSON.stringify(data)], { type: "application/json" }), `respaldo-aldam-${todayISO()}.json`);
    toast.success("Copia de seguridad descargada");
  };

  const restore = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !window.confirm("¿Restaurar este respaldo? Se reemplazarán TODOS los datos actuales.")) return;
    try {
      const payload = JSON.parse(await file.text());
      const { data } = await api.post("/restore", payload);
      toast.success("Datos restaurados");
      if (data.secret_path && data.secret_path !== settings.secret_path) return goTo(data.secret_path);
      const s = await reload();
      setF({ ...s, phones: s.phones.join("\n"), emails: s.emails.join("\n") });
    } catch (err) {
      toast.error(err instanceof SyntaxError ? "Archivo inválido" : errMsg(err));
    }
  };

  const link = `${window.location.origin}/${f.secret_path}`;

  return (
    <div>
      <PageHeader title="Configuración" subtitle="Datos de la empresa que aparecen en proformas, facturas y reportes">
        <Button className="btn-neon" onClick={save} disabled={saving} data-testid="settings-save-btn"><Save className="w-4 h-4 mr-1" />Guardar</Button>
      </PageHeader>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="panel p-5 xl:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <F label="Nombre de la empresa"><Input value={f.company_name} onChange={set("company_name")} data-testid="settings-name" /></F>
          <F label="Cédula jurídica / datos fiscales"><Input value={f.legal_id} onChange={set("legal_id")} data-testid="settings-legal-id" /></F>
          <F label="Eslogan" className="sm:col-span-2"><Input value={f.slogan} onChange={set("slogan")} data-testid="settings-slogan" /></F>
          <F label="Dirección" className="sm:col-span-2"><Input value={f.address} onChange={set("address")} data-testid="settings-address" /></F>
          <F label="Teléfonos (uno por línea)"><Textarea rows={3} value={f.phones} onChange={set("phones")} data-testid="settings-phones" /></F>
          <F label="Correos (uno por línea)"><Textarea rows={3} value={f.emails} onChange={set("emails")} data-testid="settings-emails" /></F>
          <F label="IVA por defecto %"><Input type="number" value={f.iva} onChange={set("iva")} data-testid="settings-iva" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Moneda">
              <Select value={f.currency} onValueChange={set("currency")}>
                <SelectTrigger data-testid="settings-currency"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="CRC">Colones ₡</SelectItem><SelectItem value="USD">Dólares $</SelectItem></SelectContent>
              </Select>
            </F>
            <F label="Tipo de cambio ₡/$"><Input type="number" value={f.exchange_rate} onChange={set("exchange_rate")} data-testid="settings-rate" /></F>
          </div>
          <F label="Formas de pago / cuentas (aparece en documentos)" className="sm:col-span-2"><Textarea rows={2} value={f.payment_info} onChange={set("payment_info")} data-testid="settings-payment-info" /></F>
          <F label="Términos y condiciones (pie de página)" className="sm:col-span-2"><Textarea rows={3} value={f.terms} onChange={set("terms")} data-testid="settings-terms" /></F>
        </div>

        <div className="space-y-4">
          <div className="panel p-5" data-testid="settings-logo-panel">
            <div className="label-cap mb-3">Logo de la empresa</div>
            <div className="flex items-center gap-4">
              <div className="w-28 h-28 rounded-2xl bg-white flex items-center justify-center overflow-hidden">
                {f.logo ? <img src={f.logo} alt="Logo" className="max-w-full max-h-full object-contain" data-testid="settings-logo-preview" /> : <Cpu className="w-10 h-10 text-slate-300" />}
              </div>
              <div className="flex flex-col gap-2">
                <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-[#00F0FF] hover:underline">
                  <Upload className="w-4 h-4" />Subir logo
                  <input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="settings-logo-input" />
                </label>
                {f.logo && <button className="inline-flex items-center gap-2 text-sm text-rose-400 hover:underline" onClick={() => setF({ ...f, logo: "" })} data-testid="settings-logo-remove"><Trash2 className="w-4 h-4" />Quitar</button>}
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-3">Se muestra en el menú, proformas, facturas y reportes.</p>
          </div>

          <div className="panel p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="label-cap">Envío automático por correo</div>
                <p className="text-xs text-slate-500 mt-1">Al crear una proforma o factura se envía al correo del cliente con el PDF adjunto, desde facturacion@aldamglobaltech.com. Las respuestas llegan a esa misma dirección.</p>
              </div>
              <Switch checked={f.auto_email !== false} onCheckedChange={set("auto_email")} data-testid="settings-auto-email" />
            </div>
          </div>

          <div className="panel p-5">
            <div className="label-cap mb-3">Ruta secreta de acceso</div>
            <div className="flex items-center gap-1">
              <span className="text-slate-500 text-sm">/</span>
              <Input value={f.secret_path} onChange={set("secret_path")} className="mono" data-testid="settings-secret-path" />
            </div>
            <button className="mt-2 inline-flex items-center gap-1 text-xs text-[#00F0FF] hover:underline break-all text-left" onClick={() => { navigator.clipboard?.writeText(link); toast.success("Enlace copiado"); }} data-testid="settings-copy-link">
              <Copy className="w-3 h-3 shrink-0" />{link}
            </button>
            <p className="text-xs text-amber-300/80 mt-3">Al guardar un cambio, la dirección anterior deja de funcionar. Guarde la nueva en sus favoritos.</p>
          </div>

          <div className="panel p-5">
            <div className="label-cap mb-3">Copia de seguridad</div>
            <div className="flex flex-col gap-2">
              <Button variant="outline" onClick={backup} data-testid="backup-download-btn"><DatabaseBackup className="w-4 h-4 mr-1" />Descargar respaldo</Button>
              <label className="inline-flex items-center justify-center gap-2 h-10 rounded-md border border-[#271E56] text-sm cursor-pointer hover:bg-[#1A143D]">
                <RotateCcw className="w-4 h-4" />Restaurar respaldo
                <input type="file" accept="application/json,.json" className="hidden" onChange={restore} data-testid="backup-restore-input" />
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
